import {
  Component,
  JSX,
  Show,
  createSignal,
  createUniqueId,
  onCleanup,
} from "solid-js";

export type TooltipPosition = "top" | "bottom" | "left" | "right";

export interface TooltipProps {
  content: string | JSX.Element;
  position?: TooltipPosition;
  delay?: number;
  class?: string;
  /** Block content (e.g. a list) instead of one line — wraps at a max width
   *  rather than the default `whitespace-nowrap`. Additive: existing callers
   *  are unaffected. */
  wide?: boolean;
  children: JSX.Element;
}

const positionClasses: Record<TooltipPosition, string> = {
  top: "bottom-full left-1/2 -translate-x-1/2 mb-2",
  bottom: "top-full left-1/2 -translate-x-1/2 mt-2",
  left: "right-full top-1/2 -translate-y-1/2 mr-2",
  right: "left-full top-1/2 -translate-y-1/2 ml-2",
};

const arrowClasses: Record<TooltipPosition, string> = {
  top: "top-full left-1/2 -translate-x-1/2 border-t-ink border-x-transparent border-b-transparent",
  bottom:
    "bottom-full left-1/2 -translate-x-1/2 border-b-ink border-x-transparent border-t-transparent",
  left: "left-full top-1/2 -translate-y-1/2 border-l-ink border-y-transparent border-r-transparent",
  right:
    "right-full top-1/2 -translate-y-1/2 border-r-ink border-y-transparent border-l-transparent",
};

export const Tooltip: Component<TooltipProps> = (props) => {
  const [isVisible, setIsVisible] = createSignal(false);
  const tooltipId = createUniqueId();
  const position = () => props.position ?? "top";
  const delay = () => props.delay ?? 200;

  let showTimeout: number | undefined;
  let hideTimeout: number | undefined;

  const showTooltip = () => {
    clearTimeout(hideTimeout);
    showTimeout = window.setTimeout(() => setIsVisible(true), delay());
  };

  const hideTooltip = () => {
    clearTimeout(showTimeout);
    hideTimeout = window.setTimeout(() => setIsVisible(false), 100);
  };

  onCleanup(() => {
    clearTimeout(showTimeout);
    clearTimeout(hideTimeout);
  });

  return (
    <div
      class={"relative inline-block " + (props.class ?? "")}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onFocusIn={showTooltip}
      onFocusOut={hideTooltip}
    >
      <div aria-describedby={isVisible() ? tooltipId : undefined}>
        {props.children}
      </div>

      <Show when={isVisible()}>
        <div
          id={tooltipId}
          role="tooltip"
          class={
            "absolute z-50 text-xs text-paper bg-ink rounded-sm animate-fade-in " +
            (props.wide
              ? "max-w-xs whitespace-normal px-3 py-2 "
              : "whitespace-nowrap px-2 py-1 ") +
            positionClasses[position()]
          }
        >
          {props.content}
          <div
            class={"absolute w-0 h-0 border-4 " + arrowClasses[position()]}
          />
        </div>
      </Show>
    </div>
  );
};
